import { Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { totalPatientsSeen } from '#lib/react-query/queries';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from '../Base';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '../InfoTemplateComponents.tsx';
import { useAppointmentWidget } from '../use-appointment-widget.ts';
import { StatBase } from './StatBase';

const widgetTitle = 'Total Patients Seen';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Counts the number of&nbsp;
        <InfoEmphasis>unique patients</InfoEmphasis>
        &nbsp;who attended at least one appointment during the selected time period. This metric
        helps measure patient access, service utilization, and organizational reach.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Total Patients Seen =" indented>
        <Typography variant="body2" sx={{ mb: 1, pl: 5 }}>
          Count of distinct patients with at least one appointment that is:
        </Typography>
        <InfoList
          items={[
            'Not canceled',
            'Not deleted',
            'Not marked as no-show',
            'Within the selected date range and applied filters',
          ]}
        />
      </InfoSection>

      <InfoParagraph sx={{ pl: 5, mb: 2 }}>
        All applied appointment filters (including date and other dashboard filters) are respected.
      </InfoParagraph>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            <>
              Patients with at least one appointment that:
              <InfoList
                items={[
                  'Exists in the system (not deleted)',
                  'Is not canceled',
                  'Is not marked as a no-show',
                  'Falls within the selected date range and filter criteria',
                ]}
              />
            </>,
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList items={['Canceled appointments', 'Deleted appointments', 'No-shows']} />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            <>
              A patient is counted&nbsp;
              <InfoEmphasis>once</InfoEmphasis>
              &nbsp;per date range, even if they had multiple attended appointments
            </>,
            'Patients are assumed “seen” if their appointment was neither canceled nor no-showed, even if no telehealth connection occurred',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function TotalPatientsSeen() {
  const widget = useAppointmentWidget();

  const { data, isLoading } = useQuery(totalPatientsSeen(widget.getCommonRequestParams()));

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <StatBase
      data={{
        id: 'total-patients-seen',
        title: widgetTitle,
        value: data?.value ?? null,
        valueType: 'absolute',
        trend: data?.trend
          ? {
              ...data.trend,
              daysInPeriod: widget.homeLoaderData.totalDaysInRange,
            }
          : undefined,
      }}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    />
  );
}
